/**
 * @module services/common/errorCodes
 * @description 统一错误码 → 卡片展示文案映射（纯函数，无 UI 依赖）。
 *
 * @core 图表卡片、筛选器等 API 错误态共用；后端错误码全局唯一，集中维护。
 */

import type { TResponseError } from './types';


/** 配置错误码文案的类型。 */
export type TCodeContent = {
  title?: string;
  description?: string;
};

export type TCodeLocal = {
  cn: TCodeContent;
  en: TCodeContent;
};

export type TCodeMap = Record<number, TCodeLocal>;

/** 全局错误码映射表（cn/en 双语 title / description）。 */
const ERROR_CODE_TEXT_MAP: TCodeMap = {
  53005: {
    cn: {
      title: '用户筛选器无效。',
      description: '数据集缺少用户属性，请联系报表创建人寻求帮助。',
    },
    en: {
      title: 'The user filter is not effective.',
      description:
        'The dataset lacks user attributes. Please contact the creator for help.',
    },
  },
  10013: {
    cn: {
      title: '数据源存在问题，请检查数据源。',
    },
    en: {
      title:
        'There are some issues with the data source. Please check the data source.',
    },
  },
  21116: {
    cn: {
      title: '查询失败',
      description: '数据量过大导致查询失败，请缩小时间范围后重试。',
    },
    en: {
      title: 'Query failed',
      description:
        'Query failed due to large data volume. Please narrow the time range and retry.',
    },
  },
};

/**
 * 按错误码获取卡片展示文案。
 *
 * @param error - 请求错误对象（含 code）
 * @param isEn - 是否英文
 * @returns 命中映射时返回 title/description；未命中返回 undefined
 */
export const getErrorText = (
  error: TResponseError,
  isEn: boolean,
): TCodeContent | undefined => {
  const { code } = error ?? {};
  if (code == null) {
    return undefined;
  }
  return ERROR_CODE_TEXT_MAP[code]?.[isEn ? 'en' : 'cn'];
};
